import { QueryClient, QueryKey } from '@tanstack/react-query';

interface OptimisticUpdateParams<T> {
  queryClient: QueryClient;
  queryKey: QueryKey;
  updateFn: (oldData: T) => T;
}

// Updates a single cached query matching the exact queryKey
export const optimisticQueryUpdate = async <T>({
  queryClient,
  queryKey,
  updateFn,
}: OptimisticUpdateParams<T>) => {
  await queryClient.cancelQueries({ queryKey });
  const previousData = queryClient.getQueryData<T>(queryKey);

  queryClient.setQueryData<T>(queryKey, oldData => {
    if (!oldData) return oldData;
    return updateFn(oldData);
  });

  return { previousData };
};

// Updates all cached queries matching a partial queryKey (e.g. paginated queries)
export const optimisticQueriesUpdate = async <T>({
  queryClient,
  queryKey,
  updateFn,
}: OptimisticUpdateParams<T>) => {
  await queryClient.cancelQueries({ queryKey });
  const previousData = queryClient.getQueriesData<T>({ queryKey });

  queryClient.setQueriesData<T>({ queryKey }, oldData => {
    if (!oldData) return oldData;
    return updateFn(oldData);
  });

  return { previousData };
};
